import { Module, GetterTree, ActionTree, MutationTree } from "vuex";
import { RootState, ESpoedState } from "@/types/store";
import { NamedValue, GenericReferral } from "@/types/types";
import { AZNCONNECT_API } from "@/services/aznconnect-api";
import {
  Cluster,
  Department,
  ReferralSearchOptions,
  ReferralLoadOptions,
  ReferralModel,
} from "@/types/aznconnect_api";
import moment from "moment";

export const state: ESpoedState = new ESpoedState();

const namespaced: boolean = true;

export const getters: GetterTree<ESpoedState, RootState> = {
  getClusters(state): NamedValue[] {
    return state.clusters;
  },
  getDepartments(state): NamedValue[] {
    return state.departments;
  },
  getTypes(state): NamedValue[] {
    return state.types;
  },
  getPriorities(state): NamedValue[] {
    return state.priorities;
  },
  currentCluster(state): string {
    return state.currentCluster;
  },
  currentDepartment(state): string {
    return state.currentDepartment;
  },
  currentType(state): string {
    return state.currentType;
  },
  currentPriority(state): string {
    return state.currentPriority;
  },
  referrals(state): GenericReferral[] {
    return state.referrals;
  },
};

export const actions: ActionTree<ESpoedState, RootState> = {
  loadPriorities({ commit, state }) {
    if (!state.priorities || state.priorities.length == 0) {
      // Types have not been loaded yet
      return AZNCONNECT_API.get("/Settings/Priorities").then(
        function(response) {
          // eslint-disable-next-line no-console
          console.log("Priorities loaded");
          const apiPriorities: Array<string> = response.data as Array<string>;
          const priorities: Array<NamedValue> = [];
          for (const apiPriority of apiPriorities) {
            priorities.push(new NamedValue(apiPriority, apiPriority));
          }
          commit("setPriorities", priorities);
        },
        function(response) {
          // error callback
          // eslint-disable-next-line no-console
          console.error("Unable to load priorities");
        },
      );
    }
  },
  loadTypes({ commit, state }) {
    if (!state.types || state.types.length == 0) {
      // Types have not been loaded yet
      return AZNCONNECT_API.get("/Settings/Types").then(
        function(response) {
          // eslint-disable-next-line no-console
          console.log("Types loaded");
          const apiTypes: Array<string> = response.data as Array<string>;
          const types: Array<NamedValue> = [];
          for (const apiType of apiTypes) {
            types.push(new NamedValue(apiType, apiType));
          }
          commit("setTypes", types);
        },
        function(response) {
          // error callback
          // eslint-disable-next-line no-console
          console.error("Unable to load types");
        },
      );
    }
  },
  loadClusters({ commit, state }) {
    if (!state.clusters || state.clusters.length == 0) {
      // Clusters have not been loaded yet
      return AZNCONNECT_API.get("/Settings/Clusters").then(
        function(response) {
          // eslint-disable-next-line no-console
          console.log("Locations loaded");
          const apiClusters: Array<Cluster> = response.data as Array<Cluster>;
          const clusters: Array<NamedValue> = [];
          for (const apiCluster of apiClusters) {
            clusters.push(
              new NamedValue(apiCluster.code, apiCluster.description),
            );
          }
          commit("setClusters", clusters);
        },
        function(response) {
          // error callback
          // eslint-disable-next-line no-console
          console.error("Unable to load clusters");
        },
      );
    }
  },
  loadDepartments({ commit, state }, payload) {
    let cluster: string = payload as string;

    let filterCluster = cluster == "-" || cluster == null ? "" : cluster;

    return AZNCONNECT_API.get("/Settings/Departments", {
      params: { cluster: filterCluster },
    }).then(
      function(response) {
        // eslint-disable-next-line no-console
        console.log("Departments loaded for cluster " + filterCluster);
        const apiDepartments: Array<Department> = response.data as Array<
          Department
        >;
        const departments: Array<NamedValue> = [];
        for (const apiDepartment of apiDepartments) {
          departments.push(
            new NamedValue(apiDepartment.code, apiDepartment.description),
          );
        }
        commit("setDepartments", departments);
      },
      function(response) {
        // eslint-disable-next-line no-console
        console.error("Unable to load departments");
      },
    );
  },
  loadReferrals({ state }, options: ReferralLoadOptions) {
    const isPaging = !!options.pagingOptions.datetime;

    if (!isPaging) {
      state.referrals = [];
    }

    return AZNCONNECT_API.post("/Referral/GetPaged", {
      fromDateTime: options.pagingOptions.datetime,
      length: options.pagingOptions.pageSize,
      onlyIncoming: options.patientNumbers ? false : true,
      cluster: options.cluster,
      department: options.department,
      type: options.type,
      priority: options.priority,
      patientNumbers: options.patientNumbers,
    }).then(response => {
      const apiReferrals: Array<ReferralModel> = response.data.referrals;

      return processReferralResponse(state, apiReferrals);
    });
  },
  searchReferrals({ state }, options: ReferralSearchOptions) {
    const isPaging = !!options.pagingOptions.datetime;

    // Clear the list immediately for non-paging ('new') search requests
    if (!isPaging) {
      state.referrals = [];
    }

    return AZNCONNECT_API.post("/Referral/Search", {
      cluster: options.cluster,
      department: options.department,
      type: options.type,
      priority: options.priority,
      filter: options.filterText,
      dateFrom: options.dateFrom,
      dateTo: options.dateTo,
      pageLength: options.pagingOptions.pageSize,
      pagingDateTime: options.pagingOptions.datetime,
    }).then(function(response) {
      const apiReferrals: Array<ReferralModel> = response.data;

      return processReferralResponse(state, apiReferrals);
    });
  },
  cleanReferrals({ state }) {
    if (state.referrals) {
      var yesterday = moment().subtract(1, "day");

      // Loop backwards to prevent problems when removing objects from array
      for (var i = state.referrals.length - 1; i >= 0; i--) {
        const referral: GenericReferral = state.referrals[i];
        if (referral.messageDateTime) {
          var messageTime = moment(referral.messageDateTime);
          // eslint-disable-next-line no-console
          console.debug(messageTime + " < " + yesterday);
          if (messageTime.isBefore(yesterday)) {
            const index = state.referrals.indexOf(referral);
            state.referrals.splice(index, 1);
          }
        }
      }
    }
  },
  resolveReferral({ state }, payload) {
    const fieldEncounterId: string = payload;
    const referral: GenericReferral | undefined = state.referrals.find(
      referral => referral.reference === fieldEncounterId,
    );
    if (fieldEncounterId) {
      AZNCONNECT_API.post("/Referral/Resolve/" + fieldEncounterId).then(
        function(response) {
          var responseStatus = response.status;
          if (responseStatus === 200) {
            //Close the referral to resemble server side state
            if (referral != null) {
              referral.state = "RESOLVED";
            }
          }
        },
        function(response) {
          // error callback
          // eslint-disable-next-line no-console
          console.error("Error resolving referral");
        },
      );
    }
  },
  removeReferral({ state }, fieldEncounterId: string): boolean {
    const initialLength = state.referrals.length;

    // Filter out the referral that matches the given `fieldEncounterId`
    state.referrals = state.referrals.filter(
      referral => referral.reference !== fieldEncounterId,
    );

    return state.referrals.length < initialLength;
  },
  addReferral({ state }, payload: ReferralModel) {
    const apiReferral: ReferralModel = payload;

    const referral: GenericReferral = convert(apiReferral);
    referral.isHighlighted = true;

    state.referrals = sortReferrals([...state.referrals, referral]);
  },
  updatedPatientNumber({ state }, payload) {
    const fieldEncounterId: string = payload.fieldEncounterId;
    const patientNumber: string = payload.patientNumber;

    const referral: GenericReferral | undefined = state.referrals.find(
      referral => referral.reference == fieldEncounterId,
    );
    if (referral != null) {
      referral.patientNumber = patientNumber;
    }
  },
  updateReferralOpenedState({ state }, fieldEncounterId: string) {
    const referral: GenericReferral | undefined = state.referrals.find(
      referral => referral.reference === fieldEncounterId,
    );
    if (referral != null) {
      referral.isOpened = true;
    }
  },
  updateEtaTime(
    { state },
    payload: { referenceId: string; etaDate: string; etaTime: string },
  ) {
    const referral: GenericReferral | undefined = state.referrals.find(
      referral => referral.reference === payload.referenceId,
    );
    if (referral != null) {
      referral.etaDate = payload.etaDate;
      referral.etaTime = payload.etaTime;
      // Re-sort so the list order reflects the updated ETA immediately
      state.referrals = sortReferrals([...state.referrals]);
    }
  },
};

export const mutations: MutationTree<ESpoedState> = {
  setClusters(state, payload) {
    state.clusters = payload as Array<NamedValue>;
    state.clusters.unshift(new NamedValue("-", "-- Alle clusters --"));
  },
  setDepartments(state, payload) {
    state.departments = payload as Array<NamedValue>;
    state.departments.unshift(new NamedValue("-", "-- Alle afdelingen --"));
  },
  setTypes(state, payload) {
    state.types = payload as Array<NamedValue>;
    state.types.unshift(new NamedValue("-", "-- Alle berichttypes --"));
  },
  setPriorities(state, payload) {
    state.priorities = payload as Array<NamedValue>;
    state.priorities.unshift(new NamedValue("-", "-- Alle urgenties --"));
  },
  currentCluster(state, payload) {
    state.currentCluster = payload as string;
  },
  currentDepartment(state, payload) {
    state.currentDepartment = payload as string;
  },
  currentType(state, payload) {
    state.currentType = payload as string;
  },
  currentPriority(state, payload) {
    state.currentPriority = payload as string;
  },
};

export const espoedModule: Module<ESpoedState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};

/**
 * Processes API referrals and merges them into the state.
 * Handles deduplication, sorting, and count tracking.
 * @param state - The espoed state
 * @param apiReferrals - Array of referrals from API
 * @returns Object with newlyLoaded count
 */
function processReferralResponse(
  state: ESpoedState,
  apiReferrals: Array<ReferralModel>,
): { newlyLoaded: number } {
  const countBefore = state.referrals.length;

  if (Array.isArray(apiReferrals) && apiReferrals.length) {
    const referrals: GenericReferral[] = apiReferrals.map(convert);

    // Combine and deduplicate by reference before sorting
    const combined = [...state.referrals, ...referrals];
    const uniqueReferrals: GenericReferral[] = Array.from(
      new Map(
        combined.map(r => [r.reference, r] as [string, GenericReferral]),
      ).values(),
    );

    // Sort and update state
    state.referrals = sortReferrals(uniqueReferrals);
  }

  const countAfter = state.referrals.length;
  const newlyLoaded = countAfter - countBefore;

  return { newlyLoaded };
}

/**
 * Sorts referrals by their proximity to the current time.
 * Sorting order (from top to bottom):
 * 1. Future ETAs (upcoming referrals) sorted by ascending time (soonest first)
 * 2. Past ETAs (recent referrals) sorted by descending time (most recent first)
 */
export function sortReferrals(referrals: GenericReferral[]): GenericReferral[] {
  const now = moment();

  return referrals.sort((a, b) => {
    // Parse ETA datetime by combining etaDate and etaTime
    let aDateTime = moment(
      `${a.etaDate || ""} ${a.etaTime || ""}`,
      "DD-MM-YYYY HH:mm:ss",
      true,
    );

    let bDateTime = moment(
      `${b.etaDate || ""} ${b.etaTime || ""}`,
      "DD-MM-YYYY HH:mm:ss",
      true,
    );

    // If ETA is invalid, fall back to messageDateTime for sorting
    if (!aDateTime.isValid()) {
      aDateTime = moment(a.messageDateTime);
    }

    if (!bDateTime.isValid()) {
      bDateTime = moment(b.messageDateTime);
    }

    // Check if ETAs are in the future or past
    const aIsFuture = aDateTime.isAfter(now);
    const bIsFuture = bDateTime.isAfter(now);

    // Prioritize future ETAs over past ETAs
    if (aIsFuture && !bIsFuture) return -1;
    if (!aIsFuture && bIsFuture) return 1;

    // Both are in the same category (both future or both past)
    // For future ETAs: sort ascending (soonest first)
    // For past ETAs: sort descending (most recent first)
    if (aIsFuture && bIsFuture) {
      return aDateTime.diff(bDateTime);
    } else {
      return bDateTime.diff(aDateTime);
    }
  });
}

function convert(apiReferral: ReferralModel): GenericReferral {
  const referral: GenericReferral = new GenericReferral(
    apiReferral.URN,
    apiReferral.Status,
    apiReferral.State,
    apiReferral.UrgencyCode,
    apiReferral.PatientName,
    apiReferral.PatientBirthDate,
    apiReferral.HasAmbulanceFeedback,
    apiReferral.Reference,
  );

  referral.patientNumber = apiReferral.PatientNumber;
  referral.patientNumberVerificationLink =
    apiReferral.PatientNumberVerificationLink;
  referral.patientNumberStatusMessage = apiReferral.PatientNumberStatusMessage;
  referral.patientBirthDate = apiReferral.PatientBirthDate;
  referral.gender = apiReferral.Gender;
  referral.messageDateTime = apiReferral.MessageDateTime;
  referral.messageDate = apiReferral.MessageDate;
  referral.messageTime = apiReferral.MessageTime;
  referral.etaDate = apiReferral.EtaDate;
  referral.etaTime = apiReferral.EtaTime;
  referral.ambulanceId = apiReferral.AmbulanceId;
  referral.departureLocation = apiReferral.DepartureLocation;
  referral.departurePostalCode = apiReferral.DeparturePostalCode;
  referral.sendingEntity = apiReferral.SendingEntity;
  referral.clusterCode = apiReferral.ClusterCode;
  referral.departmentCode = apiReferral.DepartmentCode;
  referral.departmentName = apiReferral.Department;
  referral.requiredSpecialism = apiReferral.RequiredSpecialism;
  referral.injury = apiReferral.Injury;
  referral.isCancelled = apiReferral.IsCancelled;
  referral.isDeprecatedVersion = apiReferral.IsDeprecatedVersion;
  referral.isOpened = apiReferral.IsOpened;

  return referral;
}
